import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { Close, Add, Delete, Payment } from "@mui/icons-material";
import theme from "../../theme/theme";

const formatDate = (d) => {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const PaymentsDialog = ({
  open,
  onClose,
  invoice,
  onAddPayment,
  onDeletePayment,
  adding,
}) => {
  const [form, setForm] = useState({
    amount: "",
    payment_date: new Date().toISOString().split("T")[0],
    payment_method: "",
    transaction_id: "",
  });
  const [errors, setErrors] = useState({});

  const repayments = invoice?.repayments || [];

  const handleAdd = () => {
    const newErrors = {};
    if (!form.amount || parseFloat(form.amount) <= 0)
      newErrors.amount = "Amount is required";
    if (!form.payment_date) newErrors.payment_date = "Date is required";
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    onAddPayment(form, () => {
      setForm({
        amount: "",
        payment_date: new Date().toISOString().split("T")[0],
        payment_method: "",
        transaction_id: "",
      });
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: theme.dialog.borderRadius, overflow: "hidden" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 700,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Payment sx={{ color: theme.palette.primary.main }} />
          Payments — {invoice?.invoice_number}
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Summary chips */}
        <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
          <Chip
            label={`Invoice: ₹${Number(invoice?.total_invoice_amount || 0).toFixed(2)}`}
            sx={{ fontWeight: 600 }}
          />
          <Chip
            label={`Received: ₹${Number(invoice?.total_received_amount || 0).toFixed(2)}`}
            color="success"
            sx={{ fontWeight: 600 }}
          />
          <Chip
            label={`Due: ₹${Number(invoice?.total_due_amount || 0).toFixed(2)}`}
            color={Number(invoice?.total_due_amount || 0) > 0 ? "error" : "default"}
            sx={{ fontWeight: 600 }}
          />
        </Box>

        {/* Payments table */}
        {repayments.length > 0 ? (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ mb: 3, borderRadius: 2 }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Sr. No</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Method</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Transaction ID</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Recorded By</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    Amount
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {repayments.map((r, i) => (
                  <TableRow key={r.id}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{formatDate(r.payment_date)}</TableCell>
                    <TableCell>{r.payment_method || "-"}</TableCell>
                    <TableCell>{r.transaction_id || "-"}</TableCell>
                    <TableCell>{r.created_by?.name || "-"}</TableCell>
                    <TableCell align="right">
                      ₹{Number(r.amount).toFixed(2)}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Delete payment" arrow placement="top">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onDeletePayment(r.id)}
                          aria-label="Delete payment"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box
            sx={{
              py: 3,
              mb: 3,
              textAlign: "center",
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No payments recorded yet
            </Typography>
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Add payment form */}
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
          Add Payment
        </Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <TextField
            size="small"
            label="Amount"
            type="number"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            error={!!errors.amount}
            helperText={errors.amount}
            sx={{ flex: 1, minWidth: 120 }}
          />
          <TextField
            size="small"
            label="Payment Date"
            type="date"
            value={form.payment_date}
            onChange={(e) =>
              setForm({ ...form, payment_date: e.target.value })
            }
            error={!!errors.payment_date}
            helperText={errors.payment_date}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: 1, minWidth: 150 }}
          />
          <TextField
            size="small"
            label="Payment Method"
            value={form.payment_method}
            onChange={(e) =>
              setForm({ ...form, payment_method: e.target.value })
            }
            placeholder="Cash, UPI, Bank..."
            sx={{ flex: 1, minWidth: 140 }}
          />
          <TextField
            size="small"
            label="Transaction ID"
            value={form.transaction_id}
            onChange={(e) =>
              setForm({ ...form, transaction_id: e.target.value })
            }
            sx={{ flex: 1, minWidth: 140 }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: theme.button.borderRadius,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Close
        </Button>
        <Button
          onClick={handleAdd}
          variant="contained"
          disabled={adding}
          startIcon={<Add />}
          sx={{
            borderRadius: theme.button.borderRadius,
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: theme.palette.primary.main,
          }}
        >
          {adding ? "Adding..." : "Add Payment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentsDialog;
